// ✅ src/components/functionality/FunctionalityForm.js
import React, { useState, useEffect } from "react";
import { addFunctionality, updateFunctionality } from "../../services/functionalityService";
import { MdClose } from "react-icons/md";

export default function FunctionalityForm({ editData, onClose, onSuccess }) {
  const isEdit = !!editData;

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    module: "",
    type: "",
    description: "",
    is_active: true,
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // ✅ Preload for edit
  useEffect(() => {
    if (isEdit && editData) {
      setFormData({
        name: editData.name || "",
        code: editData.code || "",
        module: editData.module || "",
        type: editData.type || "",
        description: editData.description || "",
        is_active: editData.is_active ?? true,
      });
    }
  }, [isEdit, editData]);

  // ✅ Input handler
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ✅ Validation helper (same as User Add/Edit)
  const validateFields = () => {
    if (!formData.name.trim()) return "Functionality name is required.";
    if (!formData.code.trim()) return "Code is required.";
    if (!formData.module.trim()) return "Module selection is required.";
    if (!formData.type.trim()) return "Type selection is required.";
    return null;
  };

  // ✅ Save handler (Add or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    const validationError = validateFields();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const payload = { ...formData };
      const res = isEdit
        ? await updateFunctionality(editData.id, payload)
        : await addFunctionality(payload);

      if (res.success) {
        setMessage(isEdit ? "Functionality updated successfully!" : "Functionality added successfully!");
        setTimeout(() => onSuccess?.(), 800);
      } else {
        setError(res.message || "Save failed. Please try again.");
      }
    } catch (err) {
      console.error("❌ handleSubmit failed:", err);
      setError("Unexpected error occurred while saving.");
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      {/* ✅ Header */}
      <div className="uf-header">
        <h5>{isEdit ? "Edit Functionality" : "Add Functionality"}</h5>
        <button onClick={onClose}>
          <MdClose />
        </button>
      </div>

      {/* ✅ Body */}
      <div className="uf-body">
        {message && <div className="alert alert-success py-2">{message}</div>}
        {error && <div className="alert alert-danger py-2">{error}</div>}

        <form className="uf-grid" onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Functionality Name *</label>
            <input
              type="text"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Code *</label>
            <input
              type="text"
              name="code"
              className="form-control"
              value={formData.code}
              onChange={handleChange}
            />
          </div>

          {/* ✅ Module Dropdown */}
          <div className="mb-3">
            <label className="form-label">Module *</label>
            <select
              name="module"
              className="form-select"
              value={formData.module}
              onChange={handleChange}
            >
              <option value="">-- Select Module --</option>
              <option value="TASKS">TASKS</option>
              <option value="REPORTS">REPORTS</option>
              <option value="EMPLOYEES">EMPLOYEES</option>
              <option value="LOCATIONS">LOCATIONS</option>
              <option value="DEPARTMENTS">DEPARTMENTS</option>
              <option value="TENANTS">TENANTS</option>
              <option value="RBAC">RBAC</option>
              <option value="Platform Setup">Platform Setup</option>
            </select>
          </div>

          {/* ✅ Type Dropdown (ENUM aligned) */}
          <div className="mb-3">
            <label className="form-label">Type *</label>
            <select
              name="type"
              className="form-select"
              value={formData.type}
              onChange={handleChange}
            >
              <option value="">-- Select Type --</option>
              <option value="menu">menu</option>
              <option value="submenu">submenu</option>
              <option value="function">function</option>
            </select>
          </div>

          <div className="col-span-2 mb-3">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              className="form-control"
              rows={3}
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Status</label>
            <div>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />{" "}
              Active
            </div>
          </div>
        </form>
      </div>

      {/* ✅ Footer */}
      <div className="uf-actions">
        <button type="button" className="btn btn-cancel" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" onClick={handleSubmit}>
          {isEdit ? "Update Functionality" : "Save Functionality"}
        </button>
      </div>
    </div>
  );
}
